import { PractitionerModel } from '@ctw/shared/api/fhir/models/practitioner';
import type { CTWState } from '@ctw/shared/context/ctw-context';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getApiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { isFhirResource } from '@ctw/shared/utils/fhir/resource-mappings';
import { QUERY_KEY_PRACTITIONER_BY_ID } from '@ctw/shared/utils/query-keys';
import type { Practitioner, Reference } from 'fhir/r4';
import { isEmpty } from 'lodash-es';

// Use their email address as the display if no practitioner reference available.
export async function getUsersPractitionerReference(ctw: CTWState): Promise<Reference> {
  const { practitionerId, email } = ctw.requestContext.authTokenState;

  if (!practitionerId) {
    return {
      display: email,
      type: 'Practitioner',
    };
  }

  const baseUrl = `${getApiServiceBaseUrl({ service: 'fqs', env: ctw.requestContext.env })}/rest`;
  const { data } = await ctw.ctwFetch(`${baseUrl}/Practitioner/${practitionerId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${ctw.requestContext.authToken}`,
    },
  });

  return {
    reference: `Practitioner/${practitionerId}`,
    type: 'Practitioner',
    display: new PractitionerModel(data as Practitioner).fullName,
  };
}

export interface UsePractitionerArgs {
  practitionerId?: string;
}

export const usePractitioner = ({ practitionerId }: UsePractitionerArgs) => {
  return useCtwQuery({
    queryId: QUERY_KEY_PRACTITIONER_BY_ID,
    queryKey: [practitionerId],
    queryFn: async ({ telemetry, ctwFetch, requestContext }) => {
      if (isEmpty(practitionerId)) {
        return null;
      }

      try {
        const baseUrl = `${getApiServiceBaseUrl({ service: 'fqs', env: requestContext.env })}/rest`;
        const { data } = await ctwFetch(`${baseUrl}/Practitioner/${practitionerId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${requestContext.authToken}`,
          },
        });

        if (!(data && isFhirResource(data))) {
          return null;
        }

        return new PractitionerModel(data as Practitioner);
      } catch (error) {
        telemetry.trackError({
          error,
          message: 'Failed to fetch pracitioner for authenticated user',
        });
        return null;
      }
    },
  });
};
